import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useAuth } from "../contexts/auth-context";
import { twMerge } from "tailwind-merge";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Clipboard, Mars, Pen, Venus, X } from "lucide-react";
import toast from "react-hot-toast";
import SelectInput from "../components/select-input";
import {
    fighterEnties,
    fighterEntryDisplayMapping,
    fighterRequestStatus,
    fighterRequestType,
    monthMap,
    months,
    nationalities,
} from "../constants";
import {
    doc,
    Timestamp,
    getDocs,
    limit,
    orderBy,
    query,
    where,
    getDoc,
    and,
} from "firebase/firestore";
import {
    calculateAge,
    capitalizeFirstLetter,
    extractTimestamp,
    formatFirestoreTimestamp,
} from "../utils/common";
import {
    FIGHTER_CONSENT_AND_RESPONSIBILITY_AGREEMENT_PATH,
    LANDING_PATH,
    PROFILE_PATH,
} from "../App";
import {
    fighterRequestsCollection,
    requestUpdateFighter,
    updateRegisterFighter,
} from "../utils/firebase";
import Preview from "../components/preview";
import FighterRecordsTable from "../components/fighter-records-table";
import { useTranslation } from "react-i18next";

export default function FighterEdit() {
    const { t } = useTranslation();
    const { id } = useParams();
    const [request, setRequest] = useState(null);
    const { currentUser, refreshUser } = useAuth();
    const fighterPictureInputButton = useRef(null);
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: currentUser.displayName ?? "",
        nationality: "",
        email: currentUser.email ?? "???",
        birthdate: null,
        age: "",
        gender: "",
        phoneNumber: "",
        emergencyContactNumber: "",
        records: {
            BOXING: { win: "", loss: "", draw: "" },
            MUAY_THAI: { win: "", loss: "", draw: "" },
            KICKBOXING: { win: "", loss: "", draw: "" },
            MMA: { win: "", loss: "", draw: "" },
        },
        icOrPassport: "",
        gymNameOrTeamName: "",
        weightClass: "",
        height: "",
        walkOutMusic: "",
        igUsername: "",
        fighterPicture: null,
    });

    const handleRecordChange = (entry, key, value) => {
        setFormData((prev) => ({
            ...prev,
            records: {
                ...prev.records,
                [entry]: {
                    ...prev.records[entry],
                    [key]: (parseInt(value) ?? 0) > 0 ? parseInt(value) : 0,
                },
            },
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        switch (name) {
            case "birthdate-year":
                const year = parseInt(value);
                if (year > 0 && year <= 9999) {
                    setFormData((prev) => {
                        const d = prev?.birthdate?.toDate() ?? new Date();
                        d.setFullYear(year);
                        return {
                            ...prev,
                            age: calculateAge(d),
                            birthdate: Timestamp.fromDate(d),
                        };
                    });
                }
                break;
            case "birthdate-month":
                const month = monthMap[capitalizeFirstLetter(value)];
                if (month !== undefined) {
                    setFormData((prev) => {
                        const d = prev?.birthdate?.toDate() ?? new Date();
                        d.setMonth(month);
                        return {
                            ...prev,
                            age: calculateAge(d),
                            birthdate: Timestamp.fromDate(d),
                        };
                    });
                }
                break;
            case "birthdate-date":
                const date = parseInt(value);
                if (date > 0 && date <= 31) {
                    setFormData((prev) => {
                        const d = prev?.birthdate?.toDate() ?? new Date();
                        d.setDate(date);
                        return {
                            ...prev,
                            age: calculateAge(d),
                            birthdate: Timestamp.fromDate(d),
                        };
                    });
                }
                break;
            case "age":
                setFormData((prev) => {
                    const now = new Date();
                    const newYear = now.getFullYear() - value;
                    const d = prev?.birthdate?.toDate() ?? new Date();
                    d.setFullYear(newYear);
                    return {
                        ...prev,
                        age: value,
                        birthdate: Timestamp.fromDate(d),
                    };
                });
                break;
            default:
                setFormData((prev) => ({ ...prev, [name]: value }));
                break;
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) {
            setFormData((prev) => ({ ...prev, [e.target.name]: null }));
            return;
        }

        const fileName = file.name;
        const fileExt = fileName.split(".").pop().toLowerCase();
        const allowedExts =
            e.target.name === "receipt"
                ? ["pdf", "jpg", "jpeg", "png", "webp"]
                : ["jpg", "jpeg", "png", "webp"];

        if (!allowedExts.includes(fileExt)) {
            toast.error(
                `Please upload a valid file type: ${allowedExts.join(", ")}`
            );
            e.target.value = "";
            return;
        }

        const maxSize = 25 * 1024 * 1024;
        if (file.size > maxSize) {
            toast.error("File size too large (max 25MB)");
            e.target.value = "";
            return;
        }

        setFormData((prev) => ({ ...prev, [e.target.name]: file }));
    };

    const handleClose = () =>
        navigate(window.history.length > 1 ? -1 : LANDING_PATH);

    const handleSubmit = useCallback(
        (e) => {
            if (!formData.fighterPicture) {
                return toast.error("Fighter Profile is required.");
            }

            toast.promise(
                requestUpdateFighter(request?.id, formData, currentUser),
                {
                    loading: "Submitting...",
                    success: () => {
                        navigate(PROFILE_PATH);
                        return id
                            ? "Update Successful"
                            : "Registration Successful";
                    },
                    error: (error) => {
                        console.error(error);
                        return id ? "Update failed" : "Registration failed";
                    },
                }
            );
        },
        [currentUser, formData, id, navigate, request]
    );

    useEffect(() => {
        const fetchData = async () => {
            if (!!id) {
                if (!currentUser.fighter) {
                    await refreshUser();
                }
                if (!!currentUser.fighter) {
                    const updateRequestSnapshot = await getDocs(
                        query(
                            fighterRequestsCollection,
                            and(
                                where("uid", "==", currentUser?.uid),
                                where(
                                    "type",
                                    "==",
                                    fighterRequestType.updateInfo
                                )
                            ),
                            limit(1),
                            orderBy("createdAt", "desc")
                        )
                    );
                    if (updateRequestSnapshot.empty) {
                        setFormData((prev) => ({
                            ...prev,
                            ...currentUser.fighter,
                        }));
                    } else {
                        setRequest({
                            ...updateRequestSnapshot.docs[0].data(),
                            id: updateRequestSnapshot.docs[0].id,
                        });
                        setFormData((prev) => ({
                            ...prev,
                            ...currentUser.fighter,
                            ...updateRequestSnapshot.docs[0].data().data,
                        }));
                    }
                } else {
                    navigate("/404");
                }
            } else {
                navigate("/404");
            }
        };
        fetchData();
    }, [currentUser.fighter, currentUser.uid, id, navigate, refreshUser]);

    return (
        <div className="page-size h-screen flex flex-col p-0 md:p-4">
            <div className="flex justify-between items-center w-full p-2">
                <div className="flex items-center gap-2">
                    <Link to={LANDING_PATH}>
                        <img
                            className="w-12"
                            src="/assets/img/logo-mfl.png"
                            alt="Logo"
                        />
                    </Link>
                    <p className="font-bold">{t("fighterEdit.title")}</p>
                </div>
                <button
                    className="btn btn-ghost btn-square"
                    onClick={handleClose}
                >
                    <X />
                </button>
            </div>

            <div className="flex-1 overflow-auto">
                <div className="p-4">
                    <div className="flex flex-col w-full gap-6">
                        {/* Fighter Picture */}
                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                            <p className="font-bold min-w-[200px]">
                                {t("fighterEdit.fields.profilePicture.title")}
                            </p>
                            <div className="w-full">
                                <div className="flex gap-2">
                                    <Preview
                                        className="w-32 h-32"
                                        src={formData.fighterPicture}
                                    />
                                    <input
                                        type="file"
                                        hidden
                                        ref={fighterPictureInputButton}
                                        name="fighterPicture"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                    />
                                    <button
                                        className="btn btn-ghost"
                                        onClick={() =>
                                            fighterPictureInputButton.current.click()
                                        }
                                    >
                                        <Pen size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                            <p className="font-bold min-w-[200px]">
                                {t("fighterEdit.fields.fullName.title")}
                            </p>
                            <div className="w-full">
                                <input
                                    type="text"
                                    name="name"
                                    className="input input-bordered w-full"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                            <p className="font-bold min-w-[200px]">
                                {t("fighterEdit.fields.email.title")}
                            </p>
                            <div className="w-full">
                                <input
                                    type="email"
                                    name="email"
                                    className="input input-bordered w-full"
                                    value={formData.email}
                                    readOnly
                                />
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                            <p className="font-bold min-w-[200px]">
                                {t("fighterEdit.fields.dateOfBirth.title")}
                            </p>
                            <div className="w-full">
                                <div className="flex flex-col md:flex-row gap-2">
                                    <input
                                        type="number"
                                        className="input input-bordered"
                                        placeholder="Date"
                                        value={
                                            extractTimestamp(formData.birthdate)
                                                ?.day || ""
                                        }
                                        name="birthdate-date"
                                        onChange={handleChange}
                                    />
                                    <SelectInput
                                        options={months}
                                        className="flex-1"
                                        defaultValue={
                                            extractTimestamp(formData.birthdate)
                                                ?.month || ""
                                        }
                                        value={
                                            extractTimestamp(formData.birthdate)
                                                ?.month || ""
                                        }
                                        onSelect={(month) =>
                                            handleChange({
                                                target: {
                                                    name: "birthdate-month",
                                                    value: month,
                                                },
                                            })
                                        }
                                    />
                                    <input
                                        type="number"
                                        className="input input-bordered"
                                        placeholder="Year"
                                        value={
                                            extractTimestamp(formData.birthdate)
                                                ?.year || ""
                                        }
                                        name="birthdate-year"
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                            <p className="font-bold min-w-[200px]">
                                {t("fighterEdit.fields.age.title")}
                            </p>
                            <div className="w-full">
                                <input
                                    type="number"
                                    name="age"
                                    className="input input-bordered w-full"
                                    value={formData.age}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                            <p className="font-bold min-w-[200px]">
                                {t("fighterEdit.fields.gender.title")}
                            </p>
                            <div className="w-full">
                                <select
                                    name="gender"
                                    className="select select-bordered w-full"
                                    value={formData.gender}
                                    onChange={handleChange}
                                >
                                    <option value="">
                                        {t(
                                            "fighterEdit.fields.gender.placeholder"
                                        )}
                                    </option>
                                    <option value="M">
                                        {t(
                                            "fighterEdit.fields.gender.options.male"
                                        )}
                                    </option>
                                    <option value="F">
                                        {t(
                                            "fighterEdit.fields.gender.options.female"
                                        )}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                            <p className="font-bold min-w-[200px]">
                                {t("fighterEdit.fields.nationality.title")}
                            </p>
                            <div className="w-full">
                                <SelectInput
                                    options={nationalities}
                                    defaultValue={formData.nationality}
                                    value={formData.nationality}
                                    onSelect={(value) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            nationality: value,
                                        }))
                                    }
                                />
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                            <p className="font-bold min-w-[200px]">
                                {t("fighterEdit.fields.phoneNumber.title")}
                            </p>
                            <div className="w-full">
                                <input
                                    type="text"
                                    name="phoneNumber"
                                    className="input input-bordered w-full"
                                    value={formData.phoneNumber}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                            <p className="font-bold min-w-[200px]">
                                {t("fighterEdit.fields.emergencyContact.title")}
                            </p>
                            <div className="w-full">
                                <input
                                    type="text"
                                    name="emergencyContactNumber"
                                    className="input input-bordered w-full"
                                    value={formData.emergencyContactNumber}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                            <p className="font-bold min-w-[200px]">
                                {t("fighterEdit.fields.idPassport.title")}
                            </p>
                            <div className="w-full">
                                <input
                                    type="text"
                                    name="icOrPassport"
                                    className="input input-bordered w-full"
                                    value={formData.icOrPassport}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                            <p className="font-bold min-w-[200px]">
                                {t("fighterEdit.fields.weightClass.title")}
                            </p>
                            <div className="w-full">
                                <select
                                    name="weightClass"
                                    className="select select-bordered w-full"
                                    value={formData.weightClass}
                                    onChange={handleChange}
                                >
                                    <option value="">
                                        Select Weight Class
                                    </option>
                                    <option value="A">
                                        Atomweight (48 kg)
                                    </option>
                                    <option value="B">
                                        Strawweight 115 lb (52.2 kg)
                                    </option>
                                    <option value="C">
                                        Flyweight 125 lb (56.7 kg)
                                    </option>
                                    <option value="D">
                                        Bantamweight 135 lb (61.2 kg)
                                    </option>
                                    <option value="E">
                                        Featherweight 145 lb (65.8 kg)
                                    </option>
                                    <option value="F">
                                        Lightweight 155 lb (70.3 kg)
                                    </option>
                                    <option value="G">
                                        Welterweight 170 lb (77.1 kg)
                                    </option>
                                    <option value="H">
                                        Middleweight 185 lb (83.9 kg)
                                    </option>
                                    <option value="I">
                                        Light Heavyweight 205 lb (93.0 kg)
                                    </option>
                                    <option value="J">
                                        Heavyweight 265 lb (120.2 kg)
                                    </option>
                                    <option value="K">
                                        {"Super Heavyweight (> 120 kg)"}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                            <p className="font-bold min-w-[200px]">
                                {t("fighterEdit.fields.height.title")}
                            </p>
                            <div className="w-full">
                                <input
                                    type="number"
                                    name="height"
                                    className="input input-bordered w-full"
                                    value={formData.height}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                            <p className="font-bold min-w-[200px]">
                                {t("fighterEdit.fields.records.title")}
                            </p>
                            <div className="w-full">
                                <FighterRecordsTable
                                    records={formData.records}
                                    handleRecordChange={handleRecordChange}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                            <p className="font-bold min-w-[200px]">
                                {t("fighterEdit.fields.gymTeam.title")}
                            </p>
                            <div className="w-full">
                                <input
                                    type="text"
                                    name="gymNameOrTeamName"
                                    className="input input-bordered w-full"
                                    value={formData.gymNameOrTeamName}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                            <p className="font-bold min-w-[200px]">
                                {t("fighterEdit.fields.walkoutMusic.title")}
                            </p>
                            <div className="w-full">
                                <input
                                    type="text"
                                    name="walkOutMusic"
                                    className="input input-bordered w-full"
                                    value={formData.walkOutMusic}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                            <p className="font-bold min-w-[200px]">
                                {t("fighterEdit.fields.instagram.title")}
                            </p>
                            <div className="w-full">
                                <input
                                    type="text"
                                    name="igUsername"
                                    className="input input-bordered w-full"
                                    value={formData.igUsername}
                                    onChange={handleChange}
                                    placeholder={t(
                                        "fighterEdit.fields.instagram.placeholder"
                                    )}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center bg-base-200 p-4">
                            <p className="font-bold">
                                {t("fighterEdit.updateRequest.title")}
                            </p>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                            <p className="font-bold min-w-[200px]">
                                {t("fighterEdit.updateRequest.lastUpdate")}
                            </p>
                            <div className="w-full">
                                {formatFirestoreTimestamp(request?.updateAt) ??
                                    "???"}
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                            <p className="font-bold min-w-[200px]">
                                {t("fighterEdit.updateRequest.status")}
                            </p>
                            <div className="w-full">
                                {request?.status ?? "N/A"}
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                            <p className="font-bold min-w-[200px]">
                                {t("fighterEdit.updateRequest.comments")}
                            </p>
                            <div className="w-full border">
                                {!request?.comments ||
                                request.comments.length < 1 ? (
                                    <div className="w-full h-full flex-center p-4">
                                        <p>
                                            {t(
                                                "fighterEdit.updateRequest.noComments"
                                            )}
                                        </p>
                                    </div>
                                ) : (
                                    request.comments.map((comment, index) => {
                                        const createdAt = !comment.createdAt
                                            ? null
                                            : extractTimestamp(
                                                  comment.createdAt
                                              );
                                        return (
                                            <div
                                                className="w-full "
                                                key={index}
                                            >
                                                <div className="chat chat-start">
                                                    <div className="chat-bubble">
                                                        <p className="">
                                                            {comment.text ?? ""}
                                                        </p>
                                                    </div>
                                                    <div className="chat-footer">
                                                        {!!createdAt && (
                                                            <p className="text-sm opacity-80">
                                                                {createdAt.day}{" "}
                                                                {
                                                                    createdAt.month
                                                                }{" "}
                                                                {createdAt.year}
                                                                ,{" "}
                                                                {createdAt.hour}
                                                                :
                                                                {
                                                                    createdAt.minute
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="mt-8">
                        <button
                            onClick={handleSubmit}
                            className="btn btn-primary w-full"
                        >
                            {t("fighterEdit.submit.button")}
                        </button>
                        <p className="">
                            {t("fighterEdit.submit.description")}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
